import React from 'react';
import { Table, Typography, Button, Select } from 'antd';
import { Colors } from '@common/tokens';
import {
  CurrencyInput,
  Icon,
  InfoTooltip,
  LoadingState,
  TimeWithTimeZone,
} from '@common/components';
import {
  MatchBreakdownType,
  MATCH_BREAKDOWN,
  MatchBreakdownQueryResponse,
} from '@scheduling/graphql';
import { useQuery } from '@common/hooks';
import { IncentiveRateDisplay } from '@scheduling/components';
import { formatTierOptionLabel } from '@scheduling/services';

const { Text } = Typography;

export function MatchBreakdownDrawerTable({
  unitId,
  shiftId,
  matchBreakdownEditMode,
  setMatchBreakdownEditMode,
  wfgOverrideRates,
  setWfgOverrideRates,
  shiftEditMode,
  canEditRates,
}: {
  unitId: string;
  shiftId: string;
  matchBreakdownEditMode: boolean;
  setMatchBreakdownEditMode: (value: boolean) => void;
  wfgOverrideRates: {
    workforceGroupId: string;
    rate: number;
    previousRate: number;
  }[];
  setWfgOverrideRates: (
    value: { workforceGroupId: string; rate: number; previousRate: number }[]
  ) => void;
  shiftEditMode: boolean;
  canEditRates: boolean;
}) {
  const { data, loading } = useQuery<MatchBreakdownQueryResponse>(
    MATCH_BREAKDOWN,
    {
      variables: { organizationUnitId: unitId, shiftGapId: shiftId },
    }
  );

  const shiftHasMatches = (data?.workforceGroupMatchBreakdown?.length || 0) > 0;

  if (loading) {
    return <LoadingState />;
  }

  const columns = [
    {
      title: 'Group',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return record.workforceGroupName;
      },
    },
    {
      title: 'Available Staff',
      dataIndex: 'rate',
      key: 'rate',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return record.availableResources;
      },
    },
    {
      title: (
        <div style={{ display: 'flex' }}>
          Rate
          {canEditRates && !shiftEditMode && shiftHasMatches && (
            <Button
              type="link"
              onClick={() => setMatchBreakdownEditMode(!matchBreakdownEditMode)}
              style={{
                height: 22,
                paddingTop: 0,
                paddingBottom: 0,
              }}
            >
              <Icon icon="pen" kind="solid" size="sm" />
            </Button>
          )}
        </div>
      ),
      dataIndex: 'avg_rate',
      key: 'avg_rate',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        const handleRateChange = (value: number) => {
          const edition = {
            workforceGroupId: record.id,
            rate: value,
            previousRate: record.averageRate,
          };
          const editedWfg = wfgOverrideRates.find(
            (wfg) => wfg.workforceGroupId === record.id
          );
          if (!editedWfg) {
            setWfgOverrideRates(wfgOverrideRates.concat([edition]));
          } else {
            setWfgOverrideRates(
              wfgOverrideRates.map((wfg) =>
                record.id === wfg.workforceGroupId ? edition : wfg
              )
            );
          }
        };

        if (matchBreakdownEditMode) {
          if (record.staticPricingTiers.length > 0) {
            return (
              <Select
                defaultValue={record.averageRate / 100}
                style={{ width: 160 }}
                onChange={handleRateChange}
              >
                {record.staticPricingTiers.map((tier) => (
                  <Select.Option key={tier.id} value={tier.payRate}>
                    {formatTierOptionLabel(
                      tier,
                      record.displayAsLumpSum,
                      record.shiftDuration
                    )}
                  </Select.Option>
                ))}
              </Select>
            );
          }

          return (
            <CurrencyInput
              autoFocus={true}
              style={{ width: 90, textAlign: 'center' }}
              placeholder={(record.averageRate / 100).toString()}
              defaultValue={record.averageRate / 100}
              onChange={(value) => {
                if (typeof value !== 'number') return;
                handleRateChange(value);
              }}
            />
          );
        } else {
          return (
            <IncentiveRateDisplay
              displayAsLumpSum={record.displayAsLumpSum}
              formattedLumpSumRate={record.formattedLumpSumRate}
              formattedPerHourRate={(record.averageRate / 100).toLocaleString(
                'en-US',
                { style: 'currency', currency: 'USD' }
              )}
              tierLabel={record.incentiveTierLabel}
            />
          );
        }
      },
    },
    {
      title: (
        <>
          Visible in App
          <InfoTooltip
            size="sm"
            leftOffset={5}
            iconColor={Colors.elm}
            title="The date the shift will be shown and available to claim for this group in the mobile app."
          />
        </>
      ),
      dataIndex: 'status',
      key: 'status',
      width: '25%',
      render: (id: string, record: MatchBreakdownType) => {
        return (
          <Text>
            <TimeWithTimeZone
              date={record.visibleDate}
              variant="short_format"
            />
          </Text>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className="match-breakdown-table"
        columns={columns}
        dataSource={data?.workforceGroupMatchBreakdown}
        pagination={false}
      />

      <style jsx>{`
        :global(.match-breakdown-section) {
          cursor: pointer;
        }
        :global(.match-breakdown-table) {
          margin-bottom: 16px;
          margin-top: 24px;
        }
        :global(.match-breakdown-table .ant-table-row) {
          cursor: default;
        }
        :global(.match-breakdown-table .ant-table-tbody > tr > td) {
          border: none;
          padding: 16px 4px 16px 8px;
        }
        :global(.match-breakdown-table .ant-table-thead > tr > th) {
          border: none;
          background-color: ${Colors.steelLightest};
          padding-top: 10px;
          padding-bottom: 10px;
          font-weight: 400;
        }
        :global(
          .match-breakdown-table .ant-table-tbody > tr.ant-table-row:hover > td
        ) {
          background: none !important;
        }
      `}</style>
    </>
  );
}
